// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Address from "./address.js"

const modelClassData = {
  "attributes": {
    "address_id": {
      "column": {
        "default": null,
        "name": "address_id",
        "null": false,
        "type": "uuid"
      },
      "name": "address_id",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "primary": {
      "column": {
        "default": false,
        "name": "primary",
        "null": false,
        "type": "boolean"
      },
      "name": "primary",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "ContactAddress",
  "collectionKey": "contact_addresses",
  "collectionName": "contact_addresses",
  "i18nKey": "contact_address",
  "camelizedLower": "contactAddress",
  "name": "ContactAddress",
  "nameDasherized": "contact-address",
  "pluralName": "contact_addresses",
  "ransackable_associations": [
    {
      "className": "Address",
      "collectionName": "addresses",
      "foreignKey": "address_id",
      "name": "address",
      "macro": "belongs_to",
      "resource_name": "Address",
      "through": null
    },
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "PhotoSession",
      "collectionName": "photo_sessions",
      "foreignKey": "contact_address_id",
      "name": "photo_sessions",
      "macro": "has_many",
      "resource_name": "PhotoSession",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "address_id",
      "column": {
        "default": null,
        "name": "address_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "contact_id",
      "column": {
        "default": null,
        "name": "contact_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "old_address_id",
      "column": {
        "default": null,
        "name": "old_address_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_contact_id",
      "column": {
        "default": null,
        "name": "old_contact_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "primary",
      "column": {
        "default": false,
        "name": "primary",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Address",
      "collectionName": "addresses",
      "foreignKey": "address_id",
      "name": "address",
      "macro": "belongs_to",
      "resource_name": "Address",
      "through": null
    }
  ],
  "paramKey": "contact_address",
  "primaryKey": "id"
}

/** Frontend model for ContactAddress. */
export default class ContactAddress extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  addressId() {
    return this.readAttributeUnderscore("address_id")
  }

  /** @returns {boolean} */
  hasAddressId() {
    const value = this.addressId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {boolean} */
  primary() {
    return this.readAttributeUnderscore("primary")
  }

  /** @returns {boolean} */
  hasPrimary() {
    const value = this.primary()

    return this._isPresent(value)
  }

  /** @returns {import("./address.js").default | null} */
  address() {
    return this._readBelongsToReflection({modelClass: Address, reflectionName: "address"})
  }

  /** @returns {Promise<import("./address.js").default | null>} */
  loadAddress() {
    if (!("addressId" in this)) throw new Error("Foreign key method wasn't defined: addressId")

    const id = this.addressId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "address",
        model: this,
        modelClass: Address
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./address.js").default | null>} */
  ensureAddressLoaded() {
    return this.ensureAssociationLoaded("address")
  }
}
